import type { BuildCheck } from '@/api/schemas';
import { humanize } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useWording } from '@/lib/useAudience';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { StatusPill, Tag } from '@/components/badges';
import { Skeleton } from '@/components/ui/skeleton';
import { checkCode, checkColor, checkIcon, checkMessage, detailRows, isFailedBlocking, showsCheckCode } from './checksModel';

export { checkCode } from './checksModel';

interface ChecksProps {
  checks: BuildCheck[] | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  pending: boolean;
}

/** Post-build verification list (screen 03 "Checks after build"). */
export function BuildChecks({ checks, isLoading, error, onRetry, pending }: ChecksProps) {
  if (isLoading && !checks) return <ChecksSkeleton />;
  if (error && !checks) return <ErrorState compact error={error} onRetry={onRetry} />;
  if (!checks || checks.length === 0) return <ChecksEmpty pending={pending} />;
  return (
    <ul className="flex flex-col gap-2 p-4 sm:p-5" data-testid="build-checks">
      {checks.map((c, i) => (
        <CheckRow key={c.id} check={c} index={i} />
      ))}
    </ul>
  );
}

/** The geometry of the check rows below: icon, key + status pill, one line of message. */
function ChecksSkeleton() {
  return (
    <ul className="flex flex-col gap-2 p-4 sm:p-5" aria-busy="true" aria-label="Loading checks" data-testid="build-checks-skeleton">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex items-start gap-3 rounded-lg border bg-secondary/40 px-3 py-2.5">
          <Skeleton className="mt-px size-5 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-14 rounded-full" />
            </div>
            <Skeleton className="h-3.5 w-3/4" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ChecksEmpty({ pending }: { pending: boolean }) {
  return (
    <EmptyState
      compact
      icon="shield"
      title={pending ? 'Checks run once the app is built' : 'No checks ran'}
      description={pending ? 'Hangar then checks the signing certificate, the app size, the environment, the app id and the version number.' : 'The build ended before the checks could run.'}
    />
  );
}

function CheckRow({ check: c, index }: { check: BuildCheck; index: number }) {
  const failedBlocking = isFailedBlocking(c);
  const say = useWording();
  const message = checkMessage(c);
  const details = detailRows(c.details).map(([k, v]): [string, string] => [k, say(v)]);
  return (
    <li
      data-testid={`build-check-${c.key}`}
      data-status={c.status}
      style={stagger(index)}
      className={cn('enter flex items-start gap-3 rounded-lg border bg-secondary/40 px-3 py-2.5', failedBlocking && 'border-failed/40 bg-failed/5')}
    >
      <Icon name={checkIcon(c.status)} size={20} className={cn('mt-px shrink-0', checkColor(c.status))} />
      <div className="min-w-0 flex-1">
        <CheckTitle check={c} failedBlocking={failedBlocking} />
        {message ? <p className="mt-0.5 break-words text-[13px] text-muted-foreground">{say(message)}</p> : null}
        {details.length > 0 ? <CheckDetails rows={details} /> : null}
      </div>
    </li>
  );
}

function CheckTitle({ check: c, failedBlocking }: { check: BuildCheck; failedBlocking: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-[13px] font-medium text-foreground">{c.key}</span>
      <StatusPill status={c.status} label={humanize(c.status)} />
      {c.blocking ? <Tag className={failedBlocking ? 'text-failed' : undefined}>blocking</Tag> : null}
      {showsCheckCode(c.status) ? <code className={cn('font-mono text-[11px]', failedBlocking ? 'text-failed' : 'text-subtle')}>{checkCode(c.key)}</code> : null}
    </div>
  );
}

function CheckDetails({ rows }: { rows: [string, string][] }) {
  return (
    <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 font-mono text-[11px] text-subtle">
      {rows.map(([k, v]) => (
        <div key={k} className="flex min-w-0 max-w-full gap-1">
          <dt className="shrink-0">{k}:</dt>
          <dd className="min-w-0 break-all text-muted-foreground">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
